// SYNC: keep in step with cognos-plugin/src/panel/promptLibrary/bindPrompt.ts
import type { Examples, ExampleRef } from "@/lib/capabilities";

/** Mirror of the backend STATIC_FALLBACK (ExampleObjectResolver) — keep in sync. */
const FALLBACK: Examples = {
  report: { name: "Q3 Forecast",
    searchPath: "/content/folder[@name='Sales']/report[@name='Q3 Forecast']",
    displayPath: "Public Folders / Sales / Q3 Forecast", isExample: true },
  folder: { name: "Reports", displayPath: "Public Folders/Finance/Reports", isExample: true },
  package: { name: "GO Sales (analysis)", displayPath: "Public Folders / GO Sales (analysis)", isExample: true },
  dataModule: { name: "GO Sales", displayPath: "Team Content / GO Sales", isExample: true },
  versionedReport: { name: "Quarterly Sales", displayPath: "Public Folders / Sales / Quarterly Sales", isExample: true },
  // recycled: deliberately absent — never a fake store-id
};

/**
 * Neutral generic phrasing for any missing field — used both when a
 * slot is entirely unresolved (e.g. the recycled slot, which stays
 * absent until a real recycle-bin object is found) and when a resolved
 * ExampleRef simply lacks the specific field being requested. Keyed by
 * field so each reads naturally in a sentence; covers all four fields
 * so no substitution ever falls through to an empty string.
 */
const RECYCLED_GENERIC: Record<string, string> = {
  storeId: "a deleted object from the recycle bin",
  name: "its original name",
  searchPath: "its saved location",
  displayPath: "its folder",
};

const TOKEN = /\{\{(report|folder|package|dataModule|versionedReport|recycled)\.(name|searchPath|storeId|displayPath)\}\}/g;

export function bindPrompt(text: string, examples: Examples | undefined): { text: string; usedFallback: boolean } {
  let usedFallback = false;
  const bound = text.replace(TOKEN, (_m, slot: string, field: string) => {
    const live = examples?.[slot as keyof Examples] as ExampleRef | undefined;
    const ref = live ?? (FALLBACK[slot as keyof Examples] as ExampleRef | undefined);
    if (!ref) { usedFallback = true; return RECYCLED_GENERIC[field] ?? ""; }
    if (ref.isExample) usedFallback = true;
    const v = (ref as Record<string, unknown>)[field];
    if (typeof v === "string" && v) return v;
    usedFallback = true;
    return RECYCLED_GENERIC[field] ?? "";
  });
  return { text: bound, usedFallback };
}
